/**
 * @module support/order-payment
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-any */ // We need to be able to allow developers send any payload the want
// Core
import { Injectable } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

// Interfaces
import { ObjectInterface } from '@support/api/common/api-response.interface';
import { APIError } from '@support/api/common/error.interface';
import { CreditCardIngenicoResponse } from '@support/ingenico/interfaces/ingenico.interface';
import { ConfigurableOrderPaymentAEMData } from '@core/aem/interfaces/shop/order-payment.interface';
import {
  ValidatePaymentResponse,
  PostValidatePaymentMethodPayload,
} from '@support/payment-options/interfaces/support-order-payment.interface';

// Services
import { ContentProviderService } from '@core/aem/services/content-provider.service';
import { API_MAP } from '@core/api/proxies/common-api-endpoints';
import { ApiGatewayService } from '@core/api/services/api-gateway-service';
import { CommonApiService } from '@support/shared/services/common-api/common-api.service';
import { FormCreationService } from '@support/shared/services/form-creation.service';
import { LoggerService } from '@core/logger/services/logger.service';
import { ObjectService } from '@support/state-object-hash-utilities/services/object.service';
// Constants
import { PAYMENT_OPTIONS } from '@support/payment-options/payment-options.constants';
import { PAYMENT_CONSTANTS } from '@support/shared/constants/order-payment.constants';
import { Api2614Response } from '@support/api/proxies/responses/api-2614.interface';
import { PostValidationModel } from '@support/shared/models/order-payment-validation.model';
import { Api2613Response } from '@support/api/proxies/responses/api-2613.interface';
import { PostNegativeFile } from '@support/shared/models/order-payment-negative-file.model';
import { KeyManagerResponse } from '@support/api/proxies/responses/api-2612.interface';
import { InStoreCardDetails } from '@support/payment/instore-payment/models/instore-card-details.model';
import { throwError as observableThrowError, Observable, Subject } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { HttpResponse } from '@angular/common/http';
import { StateStoreUtilsService } from '@support/state-object-hash-utilities/utils/state-store-utils.service';
import { PaymentInformation } from '@support/payment/online-payment/models/card-entry-form.model';
import { NegativeFileCheckPayment } from '@support/api/proxies/requests/api-144.interface';
import { SHARED_PAYMENT } from '@support/payment/shared-payment/constants/shared-payment.constants';
import { ONLINE_PAYMENT } from '@support/payment/online-payment/constants/online-payment.constants';

@Injectable()
export class SupportOrderPaymentService {
  public notifyAddressChange: Subject<boolean>;
  public notifyAddressChange$: Observable<boolean>;
  constructor(
    private apiGateway: ApiGatewayService,
    private commonApiService: CommonApiService,
    private formBuilder: UntypedFormBuilder,
    private formCreationService: FormCreationService,
    private contentProviderService: ContentProviderService,
    private logger: LoggerService,
    private objectService: ObjectService,
    private stateStoreUtilService: StateStoreUtilsService
  ) {
    this.notifyAddressChange = new Subject<boolean>();
    this.notifyAddressChange$ = this.notifyAddressChange.asObservable();
  }

  /**
   * THe Method is used to get field validation constants
   * @returns {ObjectInterface}
   */
  public getFieldValidationConstants(): ObjectInterface {
    this.logger.debug(
      PAYMENT_OPTIONS.PAYMENT_OPTIONS_MODULE,
      PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE,
      ` entering getFieldValidationConstants in ${PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE}`
    );
    return this.commonApiService.getFieldValidationConstants();
  }

  /**
   * Retrieves AEM content for the browse-services components
   * @return Observable<BrowseServiceContent>
   */
  public getConfigurableData(
    contentUrl?: string,
    prefix?: string
  ): Observable<ConfigurableOrderPaymentAEMData> {
    this.logger.debug(
      PAYMENT_OPTIONS.PAYMENT_OPTIONS_MODULE,
      PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE,
      ` entering getConfigurableData in ${PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE}`
    );
    return this.contentProviderService.getContentData(contentUrl, prefix).pipe(
      map((response: HttpResponse<object>) => {
        this.logger.debug(
          PAYMENT_OPTIONS.PAYMENT_OPTIONS_MODULE,
          PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE,
          `response received for configurable content, response: ${JSON.stringify(response)}`
        );
        return this.contentProviderService.getConfigurableData(response);
      }),
      catchError((error: APIError) => {
        this.logger.error(
          PAYMENT_OPTIONS.PAYMENT_OPTIONS_MODULE,
          PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE,
          `API error while getting Configurable content in ${PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE}
          || error: ${JSON.stringify(error)}`,
          error.code
        );
        return observableThrowError(error);
      })
    );
  }

  /**
   * Method is used to build form
   * @returns {FormGroup}
   */
  public buildPaymentForm(enteredAmount?: string): UntypedFormGroup {
    this.logger.debug(
      PAYMENT_OPTIONS.PAYMENT_OPTIONS_MODULE,
      PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE,
      ` entering buildPaymentForm in ` + PAYMENT_OPTIONS.SUPPORT_ORDER_PAYMENT_SERVICE
    );
    const fieldValidationConstants = this.getFieldValidationConstants();
    return this.formBuilder.group({
      amount: this.formCreationService.getFormControl(
        fieldValidationConstants.FORMATTED_AMOUNT,
        enteredAmount || '',
        true
      ),
      ntId: this.formCreationService.getFormControl('', '', true),
    });
  }

  /**
   * Validate Card Details
   * @param {CreditCardIngenicoResponse} data
   * @returns {Observable<validatePaymentResponse>}
   */
  public validateCardInfo(data: CreditCardIngenicoResponse): Observable<ValidatePaymentResponse> {
    const payload: PostValidatePaymentMethodPayload = {};
    if (data.isEncrypted) {
      payload.encryptionBlock = data.tracks[PAYMENT_CONSTANTS.TRACK2ORDER];
      payload.encryptionTarget = PAYMENT_CONSTANTS.TRACK2;
      payload.terminalType = PAYMENT_CONSTANTS.POS;
    } else {
      payload.tempToken = data.token;
      payload.chargeAccountNumberIndicator = 'TemporaryToken';
      payload.terminalType = 'POS';
    }
    return this.apiGateway
      .postRequest(API_MAP.API_487_postValidatePaymentMethod, payload, null, null)
      .pipe(
        map((response: HttpResponse<ValidatePaymentResponse>) => {
          return response.body;
        }),
        catchError((error: APIError) => {
          return observableThrowError(error);
        })
      );
  }

  /**
   * Calls API 2613 for Cart
   */
  public postNegativeFile(
    cardData: InStoreCardDetails | PaymentInformation
  ): Observable<Api2613Response> {
    let requestPayload: object;
    let NegativeFileCheckWrapper: NegativeFileCheckPayment;
    const token = this.objectService.get(cardData, 'token');
    const postNegativeFileInfo = new PostNegativeFile(token);
    if (cardData instanceof InStoreCardDetails) {
      requestPayload = postNegativeFileInfo;
    } else {
      const isStoredPayment = cardData.storePaymentMethodIndicator;
      const encryptedCardNumber = cardData.token ? cardData.token : cardData.encryptedCardNumber;
      const pcAlias = isStoredPayment ? cardData.cardNumber : '';
      NegativeFileCheckWrapper = this.getNegativeFileCheckWrapper(
        cardData,
        pcAlias,
        isStoredPayment,
        encryptedCardNumber
      );
      NegativeFileCheckWrapper.customerId = this.setCustomerID();
      requestPayload = Object.assign(NegativeFileCheckWrapper);
    }
    // headers = headers.append(ONE_TIME_PAYMENT_CONSTANTS.BILLING_TYPE, billingType.toLocaleUpperCase());
    const apiCall = API_MAP.API_2632_READ_NEGATIVE_FILE;
    return this.apiGateway.postRequest(apiCall, requestPayload, null, null).pipe(
      map((response: HttpResponse<Api2613Response>) => {
        return response.body;
      })
    );
  }

  public getNegativeFileCheckWrapper(
    cardData: PaymentInformation,
    pcAlias: string,
    isStoredPayment: boolean,
    encryptedCardNumber: string
  ): NegativeFileCheckPayment {
    const ctgy =
      cardData.paymentMethodCode === PAYMENT_OPTIONS.CC
        ? PAYMENT_OPTIONS.CREDIT
        : PAYMENT_OPTIONS.DEBIT;
    if (!pcAlias) {
      return {
        swipeData: {
          encryptedContent: !isStoredPayment ? encryptedCardNumber : '',
          encryptionTarget: SHARED_PAYMENT.PAN,
        },
        payment: {
          programCode: SHARED_PAYMENT.PROGRAM_CODE_CNP,
        },
        commonCustomerId: this.setCommonCustomerID(),
      };
    } else {
      return {
        payment: {
          programCode: SHARED_PAYMENT.PROGRAM_CODE_CNP,
          paymentMethodReference: {
            paymentMethodCode: cardData.paymentMethodCode,
          },
          paymentInstrument: {
            PaymentCard: {
              category: ctgy,
              paymentCardAlias: pcAlias,
            },
          },
        },
        commonCustomerId: this.setCommonCustomerID(),
      };
    }
  }

  /**
   * set customer ID for negative file check
   */
  private setCustomerID(): string {
    return this.stateStoreUtilService.getCustomerId() &&
      this.stateStoreUtilService.getCustomerId() !== ONLINE_PAYMENT.ANON
      ? this.stateStoreUtilService.getCustomerId()
      : null;
  }
  /**
   * set common customer ID for negative file check
   */
  private setCommonCustomerID(): string {
    return this.objectService.get(this.stateStoreUtilService.getAccountLinesInfo(), 'customer.ccid')
      ? this.objectService.get(this.stateStoreUtilService.getAccountLinesInfo(), 'customer.ccid')
      : null;
  }
  /**
   * Calls API 2613 for Cart
   */
  public postValidation(): Observable<Api2614Response> {
    const requestPayload = new PostValidationModel();
    // headers = headers.append(ONE_TIME_PAYMENT_CONSTANTS.BILLING_TYPE, billingType.toLocaleUpperCase());
    return this.apiGateway
      .postRequest(API_MAP.API_2614_postValidation, requestPayload, null, null)
      .pipe(
        map((response: HttpResponse<Api2614Response>) => {
          return response.body;
        })
      );
  }

  /**
   * method is used to get public key
   * @param jwk
   */
  public keyManager(jwk: string): Observable<KeyManagerResponse> {
    const payload = {
      encryptionFormat: jwk,
    };
    return this.apiGateway.postRequest(API_MAP.API_2612_keyManager, payload, null, null, null).pipe(
      map((response: HttpResponse<KeyManagerResponse>) => {
        return response.body;
      }),
      catchError((error: APIError) => {
        return observableThrowError(error);
      })
    );
  }
}
